<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card, ConfirmAction } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { api } from '../api.js'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
/** `/admin/guilds/:id`：只看这个公会的详情（在册 Agent、归档）；没有 id 是公会列表和待办申请。 */
const detailId = typeof route.params.id === 'string' ? route.params.id : null
const guilds = ref(null)
const requests = ref([])
const topups = ref([])
const agents = ref([])
const error = ref('')
const view = useListView(guilds, {
  text: (guild) => `${guild.name}\n${guild.slug}\n${guild.lead_email}`,
  sorts: [
    { value: 'name', label: t('guilds.sorts.name'), compare: (a, b) => a.name.localeCompare(b.name) },
    { value: 'level', label: t('guilds.sorts.level'), compare: (a, b) => b.level - a.level },
  ],
})

async function load() {
  const [guildList, agentList, requestList, topupList] = await Promise.all([api('GET', '/api/voko/admin/guilds'), api('GET', '/api/voko/admin/agents'), api('GET', '/api/voko/admin/guild-requests'), api('GET', '/api/voko/admin/guild-topup-requests')])
  requests.value = requestList.requests
  topups.value = topupList.requests
  guilds.value = guildList.guilds
  agents.value = agentList.agents
}

/** @param {() => Promise<void>} action */
async function run(action) {
  error.value = ''
  try {
    await action()
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

/**
 * 批准或拒绝创建公会的申请；批准即建公会，申请人成为负责人。
 * @param {string} id
 * @param {'approve'|'reject'} decision
 */
const decideRequest = (id, decision) => run(() => api('POST', `/api/voko/admin/guild-requests/${id}/decision`, { decision }))

/**
 * 批准或拒绝公会金库的充值申请；批准即入账到那个公会的金库。
 * @param {string} id
 * @param {'approve'|'reject'} decision
 */
const decideTopup = (id, decision) => run(() => api('POST', `/api/voko/admin/guild-topup-requests/${id}/decision`, { decision }))

/**
 * 运营代主人停用 / 恢复一个 Agent。
 * @param {string} id
 * @param {'active'|'suspended'} status
 */
const setStatus = (id, status) => run(() => api('POST', `/api/voko/admin/agents/${id}/status`, { status }))

/**
 * 在这个公会在册的 Agent。
 * @param {string} slug
 */
/** 归档公会（软删除：数据保留，不再接新活）。 */
const archive = async (guild) => {
  await api('POST', `/api/voko/guilds/${guild.slug}/archive`)
  await router.push('/admin/guilds')
}

const membersOf = (slug) => agents.value.filter((agent) => agent.guilds.some((item) => item.slug === slug))

onMounted(() => run(async () => {}))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.admin') }}</p>
      <h1 class="page-head__title">{{ t('adminGuilds.title') }}</h1>
    </div>
  </header>
  <p v-if="error" role="alert">{{ error }}</p>
  <Card v-if="detailId === null" class="voko-card">
    <h2>{{ t('adminGuilds.requests') }}</h2>
    <p v-if="requests.length === 0">{{ t('adminGuilds.noRequests') }}</p>
    <ul class="voko-admin-agents">
      <li v-for="request in requests" :key="request.id">
        <span><strong>{{ request.name }}</strong>（{{ request.slug }}）· {{ request.description }} · {{ t('adminGuilds.requestBy', { email: request.user_email }) }}</span>
        <Button @click="decideRequest(request.id, 'approve')">{{ t('admin.approve') }}</Button>
        <Button variant="ghost" @click="decideRequest(request.id, 'reject')">{{ t('admin.reject') }}</Button>
      </li>
    </ul>
  </Card>
  <Card v-if="detailId === null" class="voko-card">
    <h2>{{ t('adminGuilds.topups') }}</h2>
    <p v-if="topups.length === 0">{{ t('adminGuilds.noTopups') }}</p>
    <ul class="voko-admin-agents">
      <li v-for="request in topups" :key="request.id">
        <span>{{ t('adminGuilds.topupRow', { guild: request.guild_name, amount: request.amount, note: request.note, email: request.requested_by_email }) }}</span>
        <Button @click="decideTopup(request.id, 'approve')">{{ t('admin.approve') }}</Button>
        <Button variant="ghost" @click="decideTopup(request.id, 'reject')">{{ t('admin.reject') }}</Button>
      </li>
    </ul>
  </Card>
  <template v-if="guilds">
    <ListToolbar v-if="detailId === null && guilds.length > 0" :view="view" />
    <p v-if="guilds.length > 0 && view.shown.value.length === 0">{{ t('list.noMatch') }}</p>
    <Card v-for="guild in view.shown.value.filter((item) => detailId === null || item.id === detailId)" :key="guild.id" class="voko-card">
      <h2><router-link :to="`/admin/guilds/${guild.id}`">{{ guild.name }}</router-link> · {{ t('world.guildLevel', { level: guild.level }) }}</h2>
      <p>{{ t('adminGuilds.lead', { email: guild.lead_email }) }}</p>
      <p>{{ t('adminGuilds.company', { name: guild.company_agent_name }) }} · {{ t('adminGuilds.bank', guild.budget) }}</p>
      <div class="voko-admin-actions">
        <Button v-if="detailId === null" size="sm" @click="router.push(`/admin/guilds/${guild.id}`)">{{ t('agents.detail') }}</Button>
        <Button size="sm" variant="secondary" @click="router.push(`/admin/guilds/${guild.id}/edit`)">{{ t('adminGuilds.edit') }}</Button>
        <ConfirmAction v-if="guild.status === 'active'" :label="t('guilds.manageForm.archive')" :action="() => archive(guild)" :confirm-text="t('guilds.manageForm.archiveConfirm')" :cancel-label="t('guilds.manageForm.cancel')" :ok-label="t('guilds.manageForm.archive')" :pending-label="t('guilds.manageForm.working')" danger />
      </div>
      <template v-if="detailId !== null">
      <h3>{{ t('adminGuilds.agents') }}</h3>
      <p v-if="membersOf(guild.slug).length === 0">{{ t('adminGuilds.noAgents') }}</p>
      <ul class="voko-admin-agents">
        <li v-for="agent in membersOf(guild.slug)" :key="agent.id">
          <span><strong>{{ agent.name }}@{{ agent.owner_handle }}</strong> · {{ t(`agents.roles.${agent.guilds.find((item) => item.slug === guild.slug).role}`) }} · {{ agent.last_seen_at === null ? t('world.neverSeen') : new Date(agent.last_seen_at).toLocaleString() }}</span>
          <Badge :variant="agent.status === 'active' ? 'success' : 'warning'" size="sm">{{ t(`agents.status.${agent.status}`) }}</Badge>
          <Button v-if="agent.status === 'active'" variant="ghost" @click="setStatus(agent.id, 'suspended')">{{ t('agents.suspend') }}</Button>
          <Button v-else variant="ghost" @click="setStatus(agent.id, 'active')">{{ t('agents.resume') }}</Button>
        </li>
      </ul>
      </template>
    </Card>
  </template>
</template>

<style scoped>
.voko-card {
  margin-bottom: var(--drama-space-md);
}
.voko-admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
  margin-block: var(--drama-space-sm);
}
.voko-admin-agents {
  margin: 0;
  padding: 0;
  list-style: none;
}
.voko-admin-agents li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
  border-top: 1px solid var(--drama-color-border);
}
.voko-admin-agents li span:first-child {
  flex: 1 1 16em;
}
</style>
